import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, CheckCircle2, CreditCard, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Field } from "@/components/Shared";
import { RateTable, RATE_OVERLAP_MSG, overlappingRows } from "@/components/RateTable";
import { useAuth } from "@/context/AuthContext";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { api, errMsg, BUSINESS_TYPES } from "@/lib/api";

const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => thisYear + 1 - i);

const Section = ({ title, text, children }) => (
  <section className="mt-card p-5 sm:p-8 grid md:grid-cols-[240px_1fr] gap-6">
    <div>
      <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-1">{text}</p>
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const toForm = (data) => ({ ...data, mileage_rates: data.mileage_rates.map((r) => ({ ...r, rate: String(r.rate) })) });

  useEffect(() => {
    api.get("/profile").then(({ data }) => setForm(toForm(data)));
  }, []);

  if (!form) return <div className="h-64 mt-card animate-pulse" />;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setRates = (rows) => setForm((f) => ({ ...f, mileage_rates: rows, uses_irs_defaults: false }));
  const restoreIrs = async () => {
    const { data } = await api.get("/mileage-rates/irs");
    setForm((f) => ({ ...f, mileage_rates: data.map((r) => ({ ...r, rate: String(r.rate) })), uses_irs_defaults: true }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (overlappingRows(form.mileage_rates).size) return toast.error(RATE_OVERLAP_MSG);
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        name: form.name, business_name: form.business_name, business_type: form.business_type,
        tax_year: Number(form.tax_year),
        mileage_rates: form.uses_irs_defaults ? null : form.mileage_rates.map((r) => ({ start: r.start, end: r.end, rate: parseFloat(r.rate) })),
      });
      setForm(toForm(data));
      if (data.name) setUser({ ...user, name: data.name });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="settings-page">
      <PageHeader overline="Account" title="Settings" />
      <Section title="Business profile" text="Shown on your dashboard and reports.">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Your name" htmlFor="s-name">
            <Input id="s-name" value={form.name} onChange={set("name")} className="h-12 text-base" data-testid="settings-name-input" />
          </Field>
          <Field label="Business name" htmlFor="s-biz">
            <Input id="s-biz" value={form.business_name} onChange={set("business_name")} placeholder="e.g. Lone Star Hotshot LLC" className="h-12 text-base" data-testid="settings-business-name-input" />
          </Field>
          <Field label="Business type">
            <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
              <SelectTrigger className="h-12 text-base" data-testid="settings-business-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b} data-testid={`settings-business-type-${b.toLowerCase().replace(/\s+/g, "-")}`}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tax year">
            <Select value={String(form.tax_year)} onValueChange={(v) => setForm((f) => ({ ...f, tax_year: Number(v) }))}>
              <SelectTrigger className="h-12 text-base" data-testid="settings-tax-year-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`settings-tax-year-${y}`}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>
      <Section title="Mileage rates" text="Each business trip uses the rate for its date. Add a new period when the IRS announces a new rate.">
        <div className={`flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-2 ${form.uses_irs_defaults ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`} data-testid="settings-rate-status">
          {form.uses_irs_defaults ? <CheckCircle2 className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
          {form.uses_irs_defaults ? "Using the standard IRS business rates" : "Using your custom rates"}
        </div>
        <RateTable rows={form.mileage_rates} onChange={setRates} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <p className="text-xs text-slate-500">2026: $0.725/mi for Jan 1–Jun 30 and $0.76/mi from Jul 1. Check irs.gov for official rates.</p>
          <button type="button" onClick={restoreIrs} className="mt-btn-outline shrink-0" data-testid="settings-rate-reset">
            <RotateCcw className="w-4 h-4" /> Restore IRS rates
          </button>
        </div>
      </Section>
      <Section title="Subscription" text="Upgrade to a MileageTax plan.">
        <SubscriptionCard icon={CreditCard} />
      </Section>
      <Section title="Account" text="Your login details.">
        <p className="text-slate-700" data-testid="settings-account-email">Signed in as <strong>{user?.email}</strong></p>
      </Section>
      <Section title="Support" text="Questions or need help? Email us.">
        <a href="mailto:support@mileagetaxapp.com" className="mt-btn-outline w-full sm:w-auto" data-testid="settings-contact-support-link">
          <Mail className="w-4 h-4" /> Contact Support
        </a>
        <p className="text-sm text-slate-500">support@mileagetaxapp.com</p>
      </Section>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="mt-btn-primary w-full sm:w-auto sm:!px-12" data-testid="settings-save-button">
          {saving && <Loader2 className="w-5 h-5 animate-spin" />} Save Settings
        </button>
      </div>
    </form>
  );
}
